/**
 * EventLogger — buffers visit-recording events and flushes them in batches.
 *
 * - In-memory ring buffer (cap 5000). Drops oldest non-error events first when full.
 * - Flushes every 5 seconds OR when buffer reaches 200 events.
 * - Mirrors warn/error events into Sentry.addBreadcrumb so existing crash
 *   reports get the recorder context for free.
 *
 * Public API:
 *   addEvent(eventType, payload, severity?) — non-blocking; returns immediately.
 *   flush() — flush the buffer now (returns promise).
 *   start(visitId, sessionId), stop() — lifecycle.
 */
import { MonotonicClock } from "./MonotonicClock";
import type { EventSeverity, RecordedEvent } from "./types";
import { UploadQueue } from "./UploadQueue";

const RING_CAP = 5000;
const FLUSH_BATCH_THRESHOLD = 200;
const FLUSH_INTERVAL_MS = 5_000;

interface RuntimeState {
    visitId: number;
    sessionId: number;
    flushTimer: ReturnType<typeof setInterval> | null;
}

let runtime: RuntimeState | null = null;
const ring: RecordedEvent[] = [];

function dropOldestNonError(): void {
    // Find first event that isn't error severity, drop it. If everything is
    // error severity, drop the oldest (still preserves recency).
    for (let i = 0; i < ring.length; i++) {
        if (ring[i].severity !== "error") {
            ring.splice(i, 1);
            return;
        }
    }
    if (ring.length > 0) {
        ring.shift();
    }
}

async function maybeMirrorToSentry(event: RecordedEvent): Promise<void> {
    if (event.severity === "info") {
        return;
    }
    try {
        const Sentry = await import("@sentry/react-native");
        // Sentry's SeverityLevel includes "warning" not "warn".
        const sentryLevel: "warning" | "error" = event.severity === "error" ? "error" : "warning";
        Sentry.addBreadcrumb({
            category: `recorder.${event.eventType}`,
            level: sentryLevel,
            message: typeof event.payload?.message === "string" ? event.payload.message : undefined,
            data: event.payload
        });
    } catch {
        // Sentry optional in tests / dev
    }
}

export const EventLogger = {
    start(visitId: number, sessionId: number): void {
        if (runtime) {
            EventLogger.stop();
        }
        runtime = {
            visitId,
            sessionId,
            flushTimer: setInterval(() => {
                void EventLogger.flush();
            }, FLUSH_INTERVAL_MS)
        };
    },

    stop(): void {
        if (!runtime) {
            return;
        }
        if (runtime.flushTimer) {
            clearInterval(runtime.flushTimer);
        }
        runtime = null;
        // Best-effort final flush.
        void EventLogger.flush();
    },

    addEvent(
        eventType: string,
        payload: Record<string, unknown> = {},
        severity: EventSeverity = "info"
    ): void {
        const event: RecordedEvent = {
            tsMs: MonotonicClock.offsetMs(),
            wallClock: new Date().toISOString(),
            eventType,
            severity,
            payload
        };
        ring.push(event);
        while (ring.length > RING_CAP) {
            dropOldestNonError();
        }

        void maybeMirrorToSentry(event);

        if (ring.length >= FLUSH_BATCH_THRESHOLD) {
            void EventLogger.flush();
        }
    },

    async flush(): Promise<void> {
        if (!runtime) {
            return;
        }
        if (ring.length === 0) {
            return;
        }
        const events = ring.splice(0, ring.length);
        UploadQueue.enqueueEventBatch(runtime.visitId, runtime.sessionId, events);
        await UploadQueue.drain();
    },

    /** Test-only inspectors. */
    _bufferedCount(): number {
        return ring.length;
    },
    _reset(): void {
        if (runtime?.flushTimer) {
            clearInterval(runtime.flushTimer);
        }
        runtime = null;
        ring.length = 0;
    }
};
